import { useEffect, useMemo, useRef, useState } from 'react'
import type {
  AbilityAssignment,
  AbilityChoice,
  AbilityName,
  CharacterSummary,
  CreationChoices,
  CreationPlan,
  CreationProblem,
  EditCreationResult,
} from '@rollcall/contracts'
import {
  ABILITY_NAMES,
  choicesFromPicks,
  isStructuralSlot,
  selectionFromPicks,
  type AbilityIncrease,
  type PickMap,
} from '@rollcall/rules'
import { CreationRefused, type Api } from '../api.js'
import { useT } from './i18n-context.js'
import { AbilitiesField, type AbilityMethodKey } from './abilities-field.js'
import { IncreaseField } from './increase-field.js'
import { SlotField } from './slot-field.js'

/**
 * Визард создания персонажа.
 *
 * Слоты не перечислены здесь ни разу: план приходит с сервера, а тот
 * собирает его из записей контента (D23). Добавили класс в
 * `content/srd/` — визард показал его выборы, и этот файл не менялся.
 * Проверяется тестом с выдуманным слотом, которого в SRD нет.
 *
 * ── Про ошибки ────────────────────────────────────────────────────
 * Замечание всегда стоит у своего поля и всегда по-русски. Это два
 * разных требования, и делаются они по-разному.
 *
 * У своего поля — потому что слот известен: и собственная проверка
 * формы, и отказ сервера (`CreationProblem`) называют слот. Общий
 * список замечаний под формой означал бы «что-то не так, ищите сами».
 *
 * По-русски — потому что текст собирается здесь из кода замечания,
 * а не берётся из ответа сервера. Поле `message` в ответе русское,
 * но в него подставлены имя слота из источника («Skill Proficiencies»)
 * и сам выбор (`srd:athletics`): показывать это игроку нельзя.
 * Ответ читают глазами в логах, экран читает человек.
 *
 * Ошибка снимается сама: собственные проверки считаются от текущего
 * состояния на каждый рендер, а замечания сервера по слоту снимаются,
 * как только этот слот изменили.
 *
 * ── Про место в кампании ──────────────────────────────────────────
 * Места может не быть, и это не ошибка: без него получится драфт —
 * персонаж без кампании (D29). Форма от этого не меняется ни одним
 * полем: драфт не урезанный персонаж, у него те же выборы и тот же
 * лист. Разница видна только подписью под кнопкой, чтобы человек
 * не думал, что попал в чужую партию.
 */

const START_SCORE = 10

function emptyAssignment(): AbilityAssignment {
  return {
    strength: START_SCORE,
    dexterity: START_SCORE,
    constitution: START_SCORE,
    intelligence: START_SCORE,
    wisdom: START_SCORE,
    charisma: START_SCORE,
  }
}

function abilityChoice(
  method: AbilityMethodKey,
  assignment: AbilityAssignment,
  rolls: readonly number[],
): AbilityChoice {
  // Пул уходит на сервер как есть: проверяет его правило создания,
  // а не клиент (CLAUDE.md, п. 5).
  if (method === 'rolls') return { method, assignment, rolls: [...rolls] }
  return { method, assignment }
}

/** Слот незаполненного поля и слот замечания — одно и то же имя. */
type Messages = Readonly<Record<string, readonly string[]>>

function add(into: Record<string, string[]>, slot: string, message: string): void {
  ;(into[slot] ??= []).push(message)
}

/**
 * Правка выборов (D62): визард открывается на сделанных ответах.
 *
 * Не отдельная форма, а этот же визард: слоты, которых у нового
 * выбора нет, исчезают с экрана, новые приходят пустыми, и отправить
 * незаполненное нельзя. Зависимые выборы поэтому не теряются молча —
 * их или заполняют, или правка не уходит.
 */
export type WizardEdit = {
  readonly characterId: string
  readonly choices: CreationChoices
  /** Уровень: выше первого класс не меняется (D62). */
  readonly level: number
  /** За столом правка уходит мастеру, а не применяется сразу. */
  readonly seated: boolean
}

function picksOf(choices: CreationChoices): PickMap {
  const map: Record<string, string[]> = {
    class: [choices.class],
    species: [choices.species],
    background: [choices.background],
  }
  for (const pick of choices.picks) (map[pick.slot] ??= []).push(pick.value)
  return map
}

export function CreationWizard({
  api,
  seatId,
  campaignLevel = 1,
  editing = null,
  onCreated,
  onEdited = () => {},
  onCancel,
}: {
  readonly api: Api
  /** Место игрока в кампании; `null` — получится драфт (D29). */
  readonly seatId: string | null
  /**
   * Уровень, с которого кампания пускает за стол (D60); 1 — драфт
   * без кампании либо кампания, которая про это не думала.
   *
   * Это умолчание поля, а не предел: взять ниже можно всегда, выше —
   * тоже, и разбирается это при входе, а не отказом формы (D61).
   */
  readonly campaignLevel?: number
  /**
   * Создан персонаж первого уровня и цель сборки: до какого уровня
   * игрок собирался дойти. Повышения идут дальше отдельными шагами
   * (D60) — одной формой третий уровень не собирается, потому что
   * персонаж есть журнал решений.
   */
  readonly onCreated: (character: CharacterSummary, target: number) => void
  /** Правка вместо создания (D62); `null` — создаём нового. */
  readonly editing?: WizardEdit | null
  /** Правка применена или ушла мастеру. */
  readonly onEdited?: (result: EditCreationResult) => void
  readonly onCancel: () => void
}) {
  const t = useT()
  const initial = editing?.choices ?? null
  const [plan, setPlan] = useState<CreationPlan | null>(null)
  const [picks, setPicks] = useState<PickMap>(() =>
    initial === null ? {} : picksOf(initial),
  )
  const [name, setName] = useState(initial?.name ?? '')
  const [method, setMethod] = useState<AbilityMethodKey>(
    initial?.abilities.method ?? 'standard_array',
  )
  const [assignment, setAssignment] = useState<AbilityAssignment>(
    () => initial?.abilities.assignment ?? emptyAssignment(),
  )
  // Все шесть уже назначены: правка открывается на готовой раскладке.
  const [assigned, setAssigned] = useState<readonly AbilityName[]>(
    initial === null ? [] : ABILITY_NAMES,
  )
  const [rolls, setRolls] = useState<readonly number[]>(
    initial?.abilities.method === 'rolls' ? initial.abilities.rolls : [],
  )
  const [rolling, setRolling] = useState(false)
  const [rollFailure, setRollFailure] = useState<string | null>(null)
  const [increase, setIncrease] = useState<readonly AbilityIncrease[]>(
    initial?.increase ?? [],
  )
  // Цель сборки: до какого уровня дойти. Само создание всегда первого
  // уровня — дальше повышения (D60).
  const [target, setTarget] = useState(campaignLevel)
  const [problems, setProblems] = useState<readonly CreationProblem[]>([])
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Пока не отправляли — не ругаемся: пустая форма и так пустая,
  // а красные подписи на ней означали бы «вы уже всё сделали не так».
  const [checked, setChecked] = useState(false)
  const form = useRef<HTMLFormElement>(null)

  const draft = seatId === null

  // План зависит от вида, класса и предыстории: пока они не выбраны,
  // их слотов в плане ещё нет. Ключ — сама выборка, а не «что-то
  // поменялось»: лишний запрос за планом сбрасывал бы выбранное.
  const selection = useMemo(() => selectionFromPicks(picks), [picks])

  /**
   * Что из выборов влияет на план (D58).
   *
   * Не только вид, класс и предыстория: выбранная черта объявляет
   * свои выборы, и слоты этих выборов появляются только после неё.
   * Какие слоты так умеют, говорит сам план (`expands`) — клиенту
   * не приходится знать, что «Разносторонность» особенная.
   *
   * Остальные отметки план не трогают, и запрашивать его на каждый
   * выбранный навык незачем: в плане с выбранным классом лежат ещё
   * и триста с лишним заклинаний.
   */
  const planKey = useMemo(() => {
    const matters: Record<string, readonly string[]> = {}
    for (const slot of plan?.slots ?? []) {
      if (!slot.expands) continue
      const value = picks[slot.key]
      if (value !== undefined) matters[slot.key] = value
    }
    return JSON.stringify([selection, matters])
  }, [plan, picks, selection])

  // Зависимость — строковый ключ, а не сам объект: он новый
  // на каждый рендер, и по нему запрос уходил бы бесконечно.
  useEffect(() => {
    let cancelled = false
    void api
      // Место уходит вместе с выборкой: по нему сервер решает, видны
      // ли в визарде записи мастера (D30, D53).
      .creationPlan({
        ...selection,
        // Тип выборов в контракте изменяемый, у состояния — нет:
        // копия здесь, а не `as`, иначе компилятор перестанет
        // следить за формой.
        picks: Object.fromEntries(
          Object.entries(picks).map(([slot, values]) => [slot, [...values]]),
        ),
        seatId,
      })
      .then((next) => {
        if (!cancelled) setPlan(next)
      })
      .catch(() => {
        if (!cancelled) setFailure(t('errors.network'))
      })
    return () => {
      cancelled = true
    }
    // Зависимость — ключ, а не `picks` и `selection`: по объекту
    // запрос уходил бы на каждый рендер.
  }, [api, planKey, seatId, t])

  const needsAssignment = method === 'standard_array' || method === 'rolls'
  const allAssigned = assigned.length === ABILITY_NAMES.length

  /** Чего не хватает прямо сейчас: считается от состояния, не хранится. */
  const missing = useMemo(() => {
    const found: Record<string, string[]> = {}
    if (name.trim() === '') add(found, 'name', t('wizard.invalid.name'))
    for (const slot of plan?.slots ?? []) {
      if ((picks[slot.key] ?? []).length !== slot.choose) {
        add(found, slot.key, t('wizard.invalid.slot', { count: slot.choose }))
      }
    }
    if (needsAssignment && !allAssigned) {
      add(found, 'abilities', t('wizard.invalid.abilities'))
    }
    return found
  }, [name, plan, picks, needsAssignment, allAssigned, t])

  const filled = Object.keys(missing).length === 0

  /** Всё, что показывается у полей: свои проверки плюс отказ сервера. */
  const messages: Messages = useMemo(() => {
    const found: Record<string, string[]> = {}
    if (checked) {
      for (const [slot, list] of Object.entries(missing)) {
        for (const message of list) add(found, slot, message)
      }
    }
    // Замечание сервера по-русски: текст собирается по коду, а слот
    // нужен только для подстановок — сколько просили и сколько выбрано.
    for (const problem of problems) {
      const slot = plan?.slots.find((item) => item.key === problem.slot)
      add(
        found,
        problem.slot,
        t(`wizard.problems.${problem.code}`, {
          count: slot?.choose ?? 0,
          chosen: (picks[problem.slot] ?? []).length,
          defaultValue: t('wizard.problems.other'),
        }),
      )
    }
    return found
  }, [checked, missing, problems, plan, picks, t])

  const known = new Set([
    'name',
    'abilities',
    'increase',
    ...(plan?.slots ?? []).map((slot) => slot.key),
  ])
  // Замечание не про поле — отдельным местом вверху формы: спрятать
  // его некуда, а потерять нельзя.
  const loose = Object.entries(messages)
    .filter(([slot]) => !known.has(slot))
    .flatMap(([, list]) => list)

  /** Изменение слота снимает замечание сервера по нему: оно устарело. */
  const forget = (slot: string) => {
    setProblems((current) => current.filter((problem) => problem.slot !== slot))
  }

  const pick = (slot: string, values: readonly string[]) => {
    setPicks({ ...picks, [slot]: values })
    forget(slot)
  }

  /**
   * К первому полю с замечанием: без этого длинная форма молчит —
   * человек жмёт «Создать», ничего не происходит, а причина в трёх
   * экранах ниже.
   *
   * Через счётчик и эффект, а не сразу после отправки: замечания
   * появляются в разметке следующим кадром, и искать их в текущем
   * значит не находить ничего.
   */
  const [reveal, setReveal] = useState(0)

  useEffect(() => {
    if (reveal === 0) return
    const node = form.current?.querySelector<HTMLElement>('[data-invalid="true"]')
    if (node === null || node === undefined) return
    // jsdom не умеет прокрутку, и это не повод падать в тестах.
    node.scrollIntoView?.({ block: 'start' })
    const control = node.querySelector<HTMLElement>('input, select, textarea')
    ;(control ?? node).focus?.()
  }, [reveal])

  const submit = async () => {
    setChecked(true)
    // Ни пустого места, ни незаполненной формы на сервер не уходит:
    // отказ схемы читается как поломка приложения, а не как подсказка.
    if (plan === null || !filled) {
      setReveal((count) => count + 1)
      return
    }

    setBusy(true)
    setProblems([])
    setFailure(null)
    // Уходят только слоты нынешнего плана: выбор в слоте, которого
    // у нового класса нет, — не потерянный выбор, а выбор, которого
    // больше не существует (D62). На экране его уже не видно.
    const planned = new Set(plan.slots.map((slot) => slot.key))
    const live = Object.fromEntries(
      Object.entries(picks).filter(
        ([slot]) => isStructuralSlot(slot) || planned.has(slot),
      ),
    )
    const choices = choicesFromPicks({
      name,
      picks: live,
      abilities: abilityChoice(method, assignment, rolls),
      increase,
    })
    try {
      if (editing !== null) {
        onEdited(await api.editCreation(editing.characterId, choices))
        return
      }
      const character = await api.createCharacter({ seatId, choices })
      onCreated(character, target)
    } catch (error: unknown) {
      if (error instanceof CreationRefused) {
        setProblems(error.problems)
        setReveal((count) => count + 1)
      } else {
        // Текст ошибки с сервера на экран не идёт: там бывает разбор
        // схемы со словами вроде «Invalid uuid».
        setFailure(t('errors.unknown'))
      }
    } finally {
      setBusy(false)
    }
  }

  const nameInvalid = (messages['name'] ?? []).length > 0

  return (
    <section className="app-screen" aria-labelledby="wizard-title">
      <h2 id="wizard-title">
        {editing === null ? t('wizard.title') : t('edit.title')}
      </h2>
      {editing !== null && (
        <p className="app-field-hint" data-testid="edit-hint">
          {editing.seated ? t('edit.seatedHint') : t('edit.draftHint')}
          {editing.level > 1 && ` ${t('edit.classLocked', { level: editing.level })}`}
        </p>
      )}

      {failure !== null && (
        <p className="app-problems" role="alert">
          {failure}
        </p>
      )}

      {plan === null ? (
        <p>{t('wizard.loading')}</p>
      ) : (
        <form
          className="app-form"
          ref={form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          {loose.length > 0 && (
            <ul className="app-problems" role="alert" data-testid="loose-problems">
              {loose.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}

          {/* Замечание — рядом с полем, но снаружи `label`: внутри
              оно вошло бы в подпись самого поля, и голосовой доступ
              читал бы «Имя персонажа Впишите имя персонажа». Связь
              даёт `aria-describedby`. */}
          <div data-slot="name" data-invalid={nameInvalid ? 'true' : undefined}>
            <label className="app-field">
              <span>{t('wizard.name')}</span>
              <input
                type="text"
                value={name}
                aria-invalid={nameInvalid ? 'true' : undefined}
                aria-describedby={nameInvalid ? 'name-error' : undefined}
                onChange={(event) => {
                  setName(event.target.value)
                  forget('name')
                }}
              />
            </label>
            {nameInvalid && (
              <ul className="app-problems" id="name-error" role="alert">
                {(messages['name'] ?? []).map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Стартовый уровень (D60).
              Создание всегда первого уровня — тут задаётся, докуда
              идти дальше: после создания откроются экраны повышения
              подряд. Поле видно всегда, в том числе у драфта без
              кампании: игрок собирает персонажа и для себя. */}
          {editing === null && (
            <div data-slot="target-level">
              <label className="app-field app-field-narrow">
                <span>{t('wizard.targetLevel')}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={20}
                  value={target}
                  onChange={(event) => {
                    const next = Number(event.target.value)
                    setTarget(next >= 1 && next <= 20 ? next : 1)
                  }}
                />
              </label>
              <p className="app-slot-hint">
                {target > 1
                  ? t('wizard.targetHint', { count: target - 1 })
                  : t('wizard.targetOne')}
              </p>
            </div>
          )}

          {/* Слоты выбора (D52): на узком экране столбцом, на широком
              в две колонки. Слотов у первого уровня девять, и на
              ноутбуке они уходили на два экрана — выбранное вверху
              не было видно, когда выбирали внизу. */}
          <div className="app-wizard-slots">
            {plan.slots.map((slot) => (
              <SlotField
                key={slot.key}
                slot={slot}
                picked={picks[slot.key] ?? []}
                problems={messages[slot.key] ?? []}
                onChange={(values) => {
                  pick(slot.key, values)
                }}
              />
            ))}
          </div>

          <AbilitiesField
            plan={plan}
            method={method}
            assignment={assignment}
            assigned={assigned}
            rolls={rolls}
            problems={messages['abilities'] ?? []}
            onMethod={(next) => {
              // Пул у способов разный, поэтому раздача сбрасывается:
              // числа стандартного набора в броски не переезжают.
              setMethod(next)
              setAssigned([])
              forget('abilities')
            }}
            onAssignment={setAssignment}
            onAssigned={(next) => {
              setAssigned(next)
              forget('abilities')
            }}
            onRolls={setRolls}
            rolling={rolling}
            onRollPool={() => {
              setRolling(true)
              forget('abilities')
              void api
                .roll({
                  characterId: null,
                  reason: { kind: 'ability_pool', subject: null },
                  notation: null,
                  mode: 'normal',
                  manual: null,
                  hidden: false,
                  times: plan.abilities.methods.roll.count,
                })
                .then((result) => {
                  // Числа приходят с сервера и ложатся в пул как есть:
                  // раздаёт их игрок сам, ниже.
                  setRolls(result.outcomes.map((outcome) => outcome.total))
                })
                .catch(() => {
                  setRollFailure(t('errors.network'))
                })
                .finally(() => {
                  setRolling(false)
                })
            }}
          />
          {rollFailure !== null && (
            <p className="app-problems" role="alert">
              {rollFailure}
            </p>
          )}

          {plan.abilities.increase !== null && (
            <IncreaseField
              plan={plan.abilities.increase}
              increase={increase}
              problems={messages['increase'] ?? []}
              onChange={(next) => {
                setIncrease(next)
                forget('increase')
              }}
            />
          )}

          {draft && (
            <p className="app-slot-hint" data-testid="draft-notice">
              {t('wizard.draftNotice')}
            </p>
          )}

          <div className="app-actions">
            {/* Кнопка не заперта незаполненной формой: запертая кнопка
                не говорит, чего не хватает. Отправку останавливает
                проверка, а замечания появляются у полей. */}
            <button type="submit" className="app-button" disabled={busy}>
              {editing === null
                ? t('wizard.submit')
                : editing.seated
                  ? t('edit.send')
                  : t('edit.save')}
            </button>
            <button
              type="button"
              className="app-button app-button-quiet"
              onClick={onCancel}
            >
              {t('wizard.cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  )
}
